import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useModalLayer } from '../../lib/modalStack';
import { ChevronDown, Search } from 'lucide-react';
import CaseFormatSelector, { type CaseFormat, CASE_FORMATS, buildMergeTag } from './CaseFormatSelector';

export interface MergeFieldCategory {
  category: string;
  fields: { value: string; label: string; sample?: string }[];
}

interface MergeFieldPickerProps {
  categories: MergeFieldCategory[];
  onInsert: (tag: string) => void;
  /** If true, shows a single merged picker button. If false, expects external trigger. */
  standalone?: boolean;
  /** Button label when standalone */
  buttonLabel?: string;
  /** Alignment of the dropdown */
  align?: 'left' | 'right';
}

export default function MergeFieldPicker({
  categories,
  onInsert,
  standalone = true,
  buttonLabel = 'Insert Field',
  align = 'left',
}: MergeFieldPickerProps) {
  const [open, setOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [caseFormat, setCaseFormat] = useState<CaseFormat>('original');
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  // SYS-11: layer assigned by the modal stack while open (the z scale
  // has no generated named utilities in Tailwind v4 — overlays register
  // with the stack instead; see index.css).
  const layerZ = useModalLayer(open);
  const formatZ = useModalLayer(showFormatMenu);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowFormatMenu(false);
        setSelectedField(null);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;
    const q = search.toLowerCase();
    return categories
      .map(cat => ({
        ...cat,
        fields: cat.fields.filter(f => f.label.toLowerCase().includes(q) || f.value.toLowerCase().includes(q)),
      }))
      .filter(cat => cat.fields.length > 0);
  }, [categories, search]);

  const handleSelectField = useCallback((fieldValue: string) => {
    const tag = buildMergeTag(fieldValue.replace(/[{}]/g, ''), caseFormat);
    onInsert(tag);
    setOpen(false);
    setSelectedField(null);
    setSearch('');
    setCaseFormat('original');
  }, [caseFormat, onInsert]);

  const handleOpen = useCallback(() => {
    setOpen(prev => !prev);
    setShowFormatMenu(false);
    setSelectedField(null);
    setSearch('');
  }, []);

  // Track which format is currently active
  const currentFormatLabel = CASE_FORMATS.find(f => f.key === caseFormat)?.label || 'Original';

  return (
    <div ref={containerRef} className="relative inline-block">
      {standalone && (
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); handleOpen(); }}
          className="flex items-center space-x-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold rounded-lg transition-all cursor-pointer"
        >
          <span>{buttonLabel}</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      )}

      {open && (
        <div
          style={{ zIndex: layerZ }}
          className={`absolute top-full mt-1 w-80 bg-white border border-slate-200 rounded-xl shadow-xl max-h-[28rem] overflow-hidden flex flex-col animate-fade-in ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {/* Search */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search fields..."
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                autoFocus
              />
            </div>
          </div>

          {/* Format selector bar */}
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <span className="text-caption font-bold text-slate-400">Case Format:</span>
            <div className="relative">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setShowFormatMenu(prev => !prev); }}
                className="text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded-lg transition-all flex items-center space-x-1"
              >
                <span>{currentFormatLabel}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {showFormatMenu && (
                <div className="absolute right-0 top-full mt-1" style={{ zIndex: formatZ }}>
                  <CaseFormatSelector
                    selected={caseFormat}
                    onChange={(fmt) => {
                      setCaseFormat(fmt);
                      setShowFormatMenu(false);
                    }}
                    onClose={() => setShowFormatMenu(false)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Field list */}
          <div className="overflow-y-auto flex-1 p-1">
            {filteredCategories.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-slate-400">No fields match your search.</div>
            ) : (
              filteredCategories.map((cat) => (
                <div key={cat.category}>
                  <div className="px-3 py-1.5 text-caption font-bold text-slate-400 uppercase tracking-wider bg-slate-50/50">
                    {cat.category}
                  </div>
                  {cat.fields.map((f) => (
                    <button
                      key={f.value}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); handleSelectField(f.value); }}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 rounded-lg transition-all cursor-pointer flex items-center justify-between group"
                      title={`Insert ${f.label} (${currentFormatLabel})`}
                    >
                      <span className="min-w-0">
                        <span className="font-semibold block">{f.label}</span>
                        {f.sample && (
                          <span className="block text-[10px] font-normal text-slate-400 truncate">
                            e.g. {f.sample}
                          </span>
                        )}
                      </span>
                      <span className="text-caption font-mono text-slate-300 group-hover:text-indigo-400 transition-colors shrink-0 ml-2">
                        {buildMergeTag(f.value.replace(/[{}]/g, ''), caseFormat)}
                      </span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
